import type { LearnerCardProps } from '../LearnerCard';
import { useTheme } from 'styled-components';
import { DateRangeText } from '~/features/temporal';
import { getIconColor } from '~/utils';
import { LearnerCard } from '../LearnerCard';
import { buildSkillsMetadata } from '../../utils';

const { I18n } = window;

const MAX_METADATA_ITEMS_IN_CARD = 4;

export interface EventSessionCardProps extends Omit<LearnerCardProps, 'iconName' | 'iconColor' | 'metadata'> {
  isVirtual?: boolean;
  startTime?: number;
  endTime?: number;
  instructors?: string[];
  numAttendees?: number;
  location?: string | null;
  tags?: string[];
  skills?: string[];
}

export function EventSessionCard({
  isVirtual,
  startTime,
  endTime,
  instructors,
  location,
  numAttendees,
  tags,
  skills,
  actionText,
  ...props
}: EventSessionCardProps) {
  const theme = useTheme();

  const TRANSLATED_ACTION_TEXT = I18n.t('js.learner.card.action_event');
  const TRANSLATED_ONLINE_TEXT = I18n.t('js.learner.card.online');

  const metadata: LearnerCardProps['metadata'] = [];

  if (startTime && endTime) {
    metadata.push({
      iconName: 'event:regular',
      text: <DateRangeText showWeekday isLocalTime startDate={startTime} endDate={endTime} />,
    });
  }

  if (location || isVirtual) {
    const locations = [];
    if (isVirtual) {
      locations.push(TRANSLATED_ONLINE_TEXT);
    }
    if (location) {
      locations.push(location);
    }

    metadata.push({
      iconName: 'location:regular',
      text: locations.join(', '),
    });
  }

  if (numAttendees) {
    metadata.push({
      iconName: 'profile:regular',
      text: I18n.t('js.learner.events.num_attendees', {
        count: numAttendees,
      }).toLowerCase(),
    });
  }

  if (skills) {
    const skillsMetadata = buildSkillsMetadata(skills, 'md', 'js.learner.home.num_skills');
    if (skillsMetadata.length) {
      metadata.push(...skillsMetadata);
    }
  }

  if (tags && metadata.length < MAX_METADATA_ITEMS_IN_CARD) {
    const tagText = tags?.join(', ');
    if (tagText) {
      metadata.push({
        iconName: 'tag:regular',
        text: tagText,
      });
    }
  }

  if (instructors && metadata.length < MAX_METADATA_ITEMS_IN_CARD) {
    const instructorText = instructors?.join(', ');
    if (instructorText) {
      metadata.push({
        iconName: 'team:regular',
        text: instructorText,
      });
    }
  }

  const iconColor = getIconColor(theme.colors.decorative.red[500]);
  return (
    <LearnerCard
      {...props}
      iconName="event:regular"
      iconColor={iconColor}
      actionText={actionText || TRANSLATED_ACTION_TEXT}
      metadata={metadata}
    />
  );
}
